import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { posts } from "@/mocks/posts";
import { categories } from "@/mocks/categories";
import PageHero from "@/components/feature/PageHero";
import PostCard from "@/components/feature/PostCard";
import JsonLd from "@/components/feature/JsonLd";
import usePageMeta from "@/hooks/usePageMeta";

export default function Summary() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") ?? "all";
  const query = params.get("q") ?? "";

  const activeCategory = categories.find((c) => c.id === activeCat);

  const list = useMemo(() => {
    let result = [...posts].sort((a, b) => b.date.localeCompare(a.date));
    if (activeCat !== "all") {
      result = result.filter((p) => p.categoryId === activeCat);
    }
    const term = query.trim().toLowerCase();
    if (term) {
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(term) ||
          p.summary.toLowerCase().includes(term) ||
          p.tags.some((t) => t.toLowerCase().includes(term))
      );
    }
    return result;
  }, [activeCat, query]);

  const handleSelect = (catId: string) => {
    const next = new URLSearchParams(params);
    if (catId === "all") next.delete("cat");
    else next.set("cat", catId);
    setParams(next);
  };

  const handleClearQuery = () => {
    const next = new URLSearchParams(params);
    next.delete("q");
    setParams(next);
  };

  const title = activeCategory
    ? `${activeCategory.name} · 汇总清单`
    : "汇总清单 · 按主题整理的全部帖子";

  const desc = activeCategory
    ? activeCategory.description
    : "把所有帖子按主题方向整理成清单，一个话题的来龙去脉按顺序排好，方便你系统性地补完落下的内容。";

  usePageMeta({
    title: `${title} - 黑料每日`,
    description: desc,
    canonical: "https://ranqiwangyou.cn/summary",
  });

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: title,
          description: desc,
          inLanguage: "zh-CN",
          url: "https://ranqiwangyou.cn/summary",
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://ranqiwangyou.cn/" },
            { "@type": "ListItem", position: 2, name: "汇总清单" },
          ],
        }}
      />

      <PageHero
        eyebrow="汇总清单"
        title={title}
        desc={desc}
        crumbs={[
          { label: "汇总清单", to: "/summary" },
          ...(activeCategory ? [{ label: activeCategory.name }] : []),
        ]}
        meta={
          <div className="rounded-lg border border-background-300 bg-background-50 px-5 py-3 text-center">
            <p className="font-heading text-2xl font-black text-foreground-950">
              {list.length}
            </p>
            <p className="text-xs text-foreground-500">当前清单条目</p>
          </div>
        }
      />

      <section className="w-full px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <h2 className="mb-3 text-sm font-semibold tracking-wide text-foreground-500">
                按主题筛选
              </h2>
              <nav className="flex flex-row flex-wrap gap-2 lg:flex-col lg:flex-nowrap">
                <button
                  type="button"
                  onClick={() => handleSelect("all")}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-3.5 py-2.5 text-sm font-medium transition-colors lg:justify-between ${
                    activeCat === "all"
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-stack-line"></i>
                    全部方向
                  </span>
                  <span className="hidden text-xs lg:inline">{posts.length}</span>
                </button>
                {categories.map((cat) => {
                  const count = posts.filter((p) => p.categoryId === cat.id).length;
                  const isActive = activeCat === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleSelect(cat.id)}
                      className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-3.5 py-2.5 text-sm font-medium transition-colors lg:justify-between ${
                        isActive
                          ? "bg-foreground-950 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${cat.icon} text-base`}></i>
                        {cat.name}
                      </span>
                      <span className="hidden text-xs lg:inline">{count}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="mt-6 rounded-lg bg-secondary-100 p-4">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-secondary-900">
                  <i className="ri-lightbulb-line"></i>
                  小提示
                </p>
                <p className="mt-2 text-xs leading-relaxed text-secondary-800">
                  想找某件事的完整脉络，先在顶部搜索框输入关键词，再配合左侧主题筛选，基本一次就能定位。
                </p>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9">
            {query && (
              <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                <span className="text-sm text-primary-800">
                  搜索结果：「<strong>{query}</strong>」共 {list.length} 条
                </span>
                <button
                  type="button"
                  onClick={handleClearQuery}
                  className="cursor-pointer rounded-md border border-primary-300 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                >
                  清除搜索
                </button>
              </div>
            )}

            {activeCategory && (
              <div className="mb-6 flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className={`${activeCategory.icon} text-xl`}></i>
                </span>
                <div>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">
                    {activeCategory.name}
                  </h2>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                    {activeCategory.description}
                  </p>
                </div>
              </div>
            )}

            {list.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {list.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
                <i className="ri-search-2-line text-3xl text-foreground-400"></i>
                <p className="mt-3 text-sm text-foreground-500">
                  没有找到匹配的内容，换个关键词或切换主题试试。
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}